<script setup lang="ts">
const {
  modelValue = "",
  invalid = false,
  disabled = false,
  required = false,
  theme = "main",
} = defineProps<{
  modelValue?: string;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  theme?: "main" | "auth" | "overlay";
}>();

const classObject = computed(() => ({
  [`is-theme-${theme}`]: theme,
  [`is-invalid`]: invalid,
  [`is-disabled`]: disabled,
}));

const emit = defineEmits(["update:modelValue", "focus", "blur"]);

const updateValue = (event: Event) => {
  emit("update:modelValue", (event.target as HTMLTextAreaElement).value);
};
</script>

<template>
  <div class="form-textarea" :class="classObject">
    <textarea
      class="element"
      :value="modelValue"
      :disabled="disabled"
      :required="required"
      v-bind="$attrs"
      @input="updateValue"
      @focus="emit('focus')"
      @blur="emit('blur')"
    />
  </div>
</template>

<style lang="scss" scoped>
.form-textarea {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  box-sizing: border-box;
  border: solid 2px var(--main-input-border-color);
  border-radius: var(--main-border-radius);
  outline: none;
  background: var(--main-input-background-color);
  color: var(--main-input-text-color);
  transition: border-color 100ms ease;

  .element {
    width: 100%;
    height: 100%;
    border: none;
    background: none;
    font-size: 16px;
    font-family: "Manrope", sans-serif;
    appearance: none;
    resize: vertical;
    padding: 0.75em;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: rgba(0, 0, 0, 0.3);
    }
  }

  &:focus-within {
    border-color: var(--main-input-focus-color);
  }

  &.is-disabled {
    opacity: 0.5;
  }

  &.is-invalid {
    border-color: var(--main-error-color);

    .element {
      color: var(--main-error-color);
    }
  }

  &.is-theme-auth {
    border-color: var(--auth-input-border-color);
    background-color: var(--auth-input-background-color);
    color: var(--auth-input-text-color);

    &:focus-within {
      border-color: var(--auth-input-focus-color);
    }

    &.is-invalid {
      border-color: var(--auth-error-color);

      .element {
        color: var(--auth-error-color);
      }
    }
  }

  &.is-theme-overlay {
    border-color: var(--overlay-input-border-color);
    background-color: var(--overlay-input-background-color);
    color: var(--overlay-input-text-color);

    &:focus-within {
      border-color: var(--overlay-input-focus-color);
    }

    &.is-invalid {
      border-color: var(--overlay-error-color);

      .element {
        color: var(--overlay-error-color);
      }
    }
  }
}
</style>
